import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import compact from 'lodash/compact'
import { Fields } from 'models/User'
import React from 'react'
import { Link } from 'react-router-dom'
import BodyCell from 'scenes/Reports/components/Table/BodyCell'
import BodyCellContainer from 'scenes/Reports/components/Table/BodyCellContainer'
import { history } from 'store'
import { customerProfileUrl } from 'utilities/urlUtils'
import { ACTIVE_USER_PARAM, buildUrlSearch, replaceUrlSearch } from './hooks'

const t = buildTranslate('users.user_list.table')

type Props = {
  user: Fields
  isLoading: boolean
  isActive?: boolean
}

const Row = ({ user, isLoading, isActive = false }: Props) => {
  const [css] = useStyletron()
  const name = compact([user.first_name, user.last_name]).join(' ')

  const returnSearch = buildUrlSearch({ [ACTIVE_USER_PARAM]: user.uid })
  // Read by `View`'s Back link (`history.location.state.returnTo`) to come
  // back to this exact search + highlighted row instead of a bare `/users`.
  const returnTo = `${history.location.pathname}?${returnSearch}`

  // Also written onto this row's own history entry directly, for the
  // browser's back button (which pops this entry, not the Back link's
  // pushed one). Guarded like react-router-dom's own `Link.handleClick`
  // (which runs this before its navigation check) so a modified/non-primary
  // click opening a new tab doesn't rewrite the current tab's history.
  const handleClick = (event: React.MouseEvent) => {
    const isModified =
      event.metaKey || event.altKey || event.ctrlKey || event.shiftKey
    if (event.defaultPrevented || event.button !== 0 || isModified) return
    replaceUrlSearch(returnSearch)
  }

  return (
    <tr
      aria-current={isActive ? 'true' : undefined}
      className={
        isActive
          ? css({
              backgroundColor: palette.belizeHole10,
              // A left border alongside the tint so the highlight isn't
              // conveyed by color alone (WCAG 1.4.1) — visible as a distinct
              // edge even where the color itself isn't perceptible.
              borderLeft: `3px solid ${palette.belizeHole}`,
            })
          : undefined
      }
    >
      <BodyCell
        isErrored={false}
        isLoading={isLoading}
        values={[{ value: name }]}
      />
      <BodyCellContainer
        className={'text-right'}
        isErrored={false}
        isLoading={isLoading}
      >
        <Link
          to={{ pathname: customerProfileUrl(user.uid), state: { returnTo } }}
          onClick={handleClick}
        >
          <Text.BodyText4 tag="span" className="ml-xs mb-0" color="primary">
            {t('view_profile')}
          </Text.BodyText4>
        </Link>
      </BodyCellContainer>
    </tr>
  )
}

export default Row
